/**
 * Iter 192 — Brand → Projects sub-tab + External Escalation Matrix.
 *
 * Renders a simple projects table for the brand and the brand-level
 * (external / client-side) escalation ladder editor underneath.
 *
 * The projects list intentionally uses the same visual grammar as the
 * global Projects page for a familiar feel (see /app/projects).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  FolderKanban, Plus, ArrowUpRight, TrendingUp, ShieldAlert, Trash2,
  ArrowUp, ArrowDown, Loader2,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { useNavigate } from "react-router-dom";

const HEALTH_TONE = {
  on_track: "border-emerald-500 text-emerald-800 bg-emerald-50",
  at_risk: "border-amber-500 text-amber-800 bg-amber-50",
  off_track: "border-rose-500 text-rose-800 bg-rose-50",
  blocked: "border-rose-500 text-rose-800 bg-rose-50",
};

export default function BrandProjectsTab({ brand }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${brand.workspace_id}/brands/${brand.id}/projects`);
      setProjects(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load projects")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (brand?.id) load(); }, [brand?.id]);

  const create = async () => {
    const nm = newName.trim();
    if (!nm) { toast.error("Give the project a name"); return; }
    if (!brand.client_id) {
      toast.error("This brand is not linked to a client. Link a client first.");
      return;
    }
    setCreating(true);
    try {
      await api.post(`/clients/${brand.client_id}/projects`,
                        { name: nm, client_id: brand.client_id,
                          brand_id: brand.id });
      toast.success("Project created");
      setNewName("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-5" data-testid="brand-projects-tab">
      <div className="border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-muted/20">
          <FolderKanban className="w-3.5 h-3.5 text-primary" />
          <span className="text-[10px] uppercase tracking-widest font-semibold">
            Projects linked to this brand
          </span>
          <Badge variant="outline" className="rounded-none text-[10px]">{projects.length}</Badge>
          <div className="ml-auto flex items-center gap-1.5">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && create()}
                     className="h-8 w-56 rounded-none text-xs"
                     placeholder="Quick add project…"
                     data-testid="brand-projects-new-name" />
            <Button size="sm" className="rounded-none h-8"
                      onClick={create} disabled={creating || !newName.trim()}
                      data-testid="brand-projects-create-btn">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Add
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/20">
              <tr className="text-left">
                <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Project</th>
                <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Status</th>
                <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Health</th>
                <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Client</th>
                <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Effort</th>
                <th className="p-2 font-semibold uppercase text-[10px] tracking-widest">Updated</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && projects.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground italic">
                  No projects linked to this brand yet. Add one above or link an existing project
                  from the Projects page.
                </td></tr>
              )}
              {projects.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-muted/10"
                        data-testid={`brand-projects-row-${p.id}`}>
                  <td className="p-2 font-medium">{p.name}</td>
                  <td className="p-2 text-muted-foreground">{p.status || "—"}</td>
                  <td className="p-2">
                    <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${HEALTH_TONE[p.health] || "border-border bg-muted/30 text-muted-foreground"}`}>
                      {p.health || "—"}
                    </span>
                  </td>
                  <td className="p-2 text-muted-foreground">{p.client?.name || "—"}</td>
                  <td className="p-2 font-mono text-[10px]">
                    {p.actual_hours ? `${Number(p.actual_hours).toFixed(1)} / ${p.estimated_hours || "—"}h` : "—"}
                  </td>
                  <td className="p-2 text-muted-foreground text-[10px]">
                    {fmtRelative(p.updated_at || p.created_at)}
                  </td>
                  <td className="p-2 text-right">
                    <button type="button" onClick={() => nav(`/app/projects/${p.id}`)}
                                className="inline-flex items-center gap-1 text-primary hover:underline text-[11px]"
                                data-testid={`brand-projects-open-${p.id}`}>
                      Open <ArrowUpRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* External escalation matrix */}
      <ExternalEscalationEditor brand={brand} />
    </div>
  );
}

function ExternalEscalationEditor({ brand }) {
  const [ladder, setLadder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [contacts, setContacts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [em, cc] = await Promise.all([
        api.get(`/brands/${brand.id}/escalation-matrix`),
        api.get(`/clients/${brand.client_id}/contacts`)
          .catch(() => ({ data: { rows: [] } })),
      ]);
      setLadder(em.data.ladder || []);
      setContacts((cc.data && (cc.data.rows || cc.data)) || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load escalation matrix")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (brand?.id) load(); }, [brand?.id]);

  const upd = (i, k, v) => {
    const next = [...ladder]; next[i] = { ...next[i], [k]: v };
    setLadder(next);
  };
  const applyContact = (i, cid) => {
    const c = contacts.find(x => x.id === cid);
    if (!c) { upd(i, "contact_id", null); return; }
    const next = [...ladder];
    next[i] = {
      ...next[i], contact_id: cid,
      name: c.name || next[i].name,
      email: c.email || next[i].email,
      phone: c.phone || next[i].phone,
    };
    setLadder(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= ladder.length) return;
    const next = [...ladder];
    [next[i], next[j]] = [next[j], next[i]];
    setLadder(next.map((s, idx) => ({ ...s, level: idx + 1 })));
  };
  const remove = (i) => setLadder(ladder.filter((_, j) => j !== i)
                              .map((s, idx) => ({ ...s, level: idx + 1 })));
  const add = () => setLadder([...ladder, {
    level: ladder.length + 1, name: "", email: "", phone: "",
    reason: "", notify_after_hours: 24, contact_id: null,
  }]);

  const save = async () => {
    // Validate names
    for (const s of ladder) {
      if (!s.name || !s.name.trim()) {
        toast.error("Every level needs a name");
        return;
      }
    }
    setBusy(true);
    try {
      const r = await api.put(`/brands/${brand.id}/escalation-matrix`,
                                    { ladder });
      setLadder(r.data.ladder);
      toast.success("External escalation matrix saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card" data-testid="brand-external-escalation">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-muted/20">
        <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
        <span className="text-[10px] uppercase tracking-widest font-semibold">
          External escalation ladder · client-side
        </span>
        <span className="text-[10px] text-muted-foreground">
          Who to loop in if the client team isn&apos;t responding
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                    onClick={add} data-testid="brand-escalation-add">
            <Plus className="w-3 h-3 mr-1" /> Add level
          </Button>
          <Button size="sm" className="rounded-none h-7 text-xs"
                    onClick={save} disabled={busy}
                    data-testid="brand-escalation-save">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {loading && <div className="text-xs text-muted-foreground text-center p-4">Loading…</div>}
        {!loading && ladder.length === 0 && (
          <div className="text-xs text-muted-foreground italic text-center p-6 border border-dashed border-border">
            No escalation ladder set. Add levels so the team knows who to escalate to
            (L1 → L2 → L3) when the client isn&apos;t responding.
          </div>
        )}
        {ladder.map((s, i) => (
          <div key={i} className="border border-border bg-white p-2 grid grid-cols-[40px_1fr_1fr_1fr_100px_auto] gap-1.5 items-end"
                    data-testid={`brand-escalation-row-${i}`}>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Level</Label>
              <div className="h-8 flex items-center justify-center font-mono font-bold text-lg border border-primary bg-primary/10 text-primary">
                L{s.level}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Contact</Label>
              <select value={s.contact_id || ""}
                         onChange={(e) => applyContact(i, e.target.value)}
                         className="h-8 rounded-none border border-border bg-background text-xs px-1 w-full"
                         data-testid={`brand-escalation-contact-${i}`}>
                <option value="">— free-form —</option>
                {contacts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.title ? ` · ${c.title}` : ""}</option>
                ))}
              </select>
              <Input value={s.name || ""}
                       onChange={(e) => upd(i, "name", e.target.value)}
                       className="h-7 rounded-none text-xs mt-1"
                       placeholder="Name" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Email</Label>
              <Input value={s.email || ""}
                       onChange={(e) => upd(i, "email", e.target.value)}
                       className="h-8 rounded-none text-xs" placeholder="name@client.com" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Reason to escalate</Label>
              <Input value={s.reason || ""}
                       onChange={(e) => upd(i, "reason", e.target.value)}
                       className="h-8 rounded-none text-xs"
                       placeholder="e.g. No response &gt; 24h" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Notify after</Label>
              <Input type="number" min={0}
                       value={s.notify_after_hours ?? ""}
                       onChange={(e) => upd(i, "notify_after_hours", e.target.value === "" ? null : Number(e.target.value))}
                       className="h-8 rounded-none text-xs font-mono" placeholder="hrs" />
            </div>
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => move(i, -1)}
                          disabled={i === 0}
                          className="h-3.5 text-muted-foreground hover:text-primary disabled:opacity-30"
                          data-testid={`brand-escalation-up-${i}`}>
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => move(i, 1)}
                          disabled={i === ladder.length - 1}
                          className="h-3.5 text-muted-foreground hover:text-primary disabled:opacity-30"
                          data-testid={`brand-escalation-down-${i}`}>
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => remove(i)}
                          className="h-3.5 text-rose-600 hover:text-rose-700"
                          data-testid={`brand-escalation-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
